/**
 * Registre des vues : une fabrique par clé de navigation déclarée dans les
 * manifestes. `satisfies Record<CleNavigation, …>` fait échouer la compilation
 * si un manifeste déclare une entrée sans vue, ou l'inverse.
 */

import type { ParametresSimulateur } from '@scc/core';
import type { CleNavigation } from '@scc/modules';
import type { Charge, Identite } from '../api.js';
import { vueTableauBord } from './tableau-bord.js';
import { vueExercice } from './exercice.js';
import { vueBudget } from './budget.js';
import { vueBudget2027 } from './budget-2027.js';
import { vuePrevisionnel } from './previsionnel.js';
import { vueMasseSalariale } from './masse-salariale.js';
import { vueAdherents } from './adherents.js';
import { vueSynthese } from './synthese.js';
import { vueAlertes } from './alertes.js';
import { vueTresorerie } from './tresorerie.js';
import { vueVeille } from './veille.js';
import { vueExerciceCompare } from './exercice-compare.js';
import { vueScenarios } from './scenarios.js';
import { vueRemunerations } from './remunerations.js';
import { vueAdministration } from './administration.js';
import { vueModules } from './modules.js';
import { vueArbitrage, vueRegles, vueSousPostes } from '../modules/classement/public.js';
import { vueJustificatifs } from '../modules/justificatifs/public.js';

export interface ContexteVue {
  readonly d: Charge;
  readonly identite: Identite;
  readonly parametresInitiaux: Partial<ParametresSimulateur> | undefined;
  /** Aller à une autre vue. */
  readonly allerA: (cle: string) => void;
  /** Recharger le tableau de bord puis revenir sur une vue ; `trajectoire` change l'hypothèse salariale. */
  readonly recharger: (page?: string, trajectoire?: string) => void;
}

export type FabriqueVue = (c: ContexteVue) => HTMLElement;

export const VUES = {
  'tableau-bord': (c) => vueTableauBord(c.d, (cle) => { c.allerA(cle); }),
  exercice: (c) =>
    vueExercice(c.d, {
      peutModifier: c.identite.role === 'administration',
      recharger: () => { c.recharger('exercice'); },
    }),
  synthese: (c) => vueSynthese(c.d),
  budget: (c) => vueBudget(c.d, () => { c.recharger('budget'); }),
  'budget-2027': (c) => vueBudget2027(() => { c.recharger('budget-2027'); }),
  'exercice-compare': (c) => vueExerciceCompare(c.d, (cle) => { c.allerA(cle); }),
  trajectoires: (c) => vuePrevisionnel(c.d, c.parametresInitiaux),
  tresorerie: (c) => vueTresorerie(c.d),
  veille: (c) => vueVeille(c.d),
  'masse-salariale': (c) => vueMasseSalariale(c.d, (cle) => { c.recharger('masse-salariale', cle); }),
  adherents: (c) => vueAdherents(c.d),
  alertes: (c) => vueAlertes(c.d),
  scenarios: (c) => vueScenarios(c.d, c.identite),
  remunerations: (c) => vueRemunerations(c.d, c.identite, (cle) => { c.recharger('remunerations', cle); }),
  exploitation: (c) => vueAdministration(c.d),
  modules: () => vueModules(),
  arbitrage: (c) => vueArbitrage(c.identite, () => { c.recharger('arbitrage'); }),
  regles: (c) => vueRegles(c.identite),
  'sous-postes': (c) => vueSousPostes(c.identite),
  justificatifs: (c) => vueJustificatifs(c.identite),
} satisfies Record<CleNavigation, FabriqueVue>;

export function fabriqueDe(cle: string): FabriqueVue | undefined {
  return Object.hasOwn(VUES, cle) ? VUES[cle as CleNavigation] : undefined;
}
